import React, { useEffect, useState, useMemo } from 'react';
import './MaintenanceRepairLogList.css';
import { MaintenanceLogType } from '../interfaces/MaintenanceLogType';
import MaintenanceRepairDetailModal from './MaintenanceRepairDetailModal';
import LoadingSpinner from './LoadingSpinner';
import {
  deleteLogById,
  getLogCount,
  loadLogs,
  saveLogs,
} from '../firebase/db-logs';
import {
  deleteMaintenanceLog,
  getMaintenanceLogCount,
  loadMaintenanceLog,
  loadMaintenanceLogs,
  saveMaintenanceLogs,
} from '../firebase/db-maintenanceLogs';
import { UserType } from '../interfaces/SettingsType';

interface MaintenanceRepairListProps {
  itemsPerPage?: number;
  isAdmin?: boolean;
  pilots: UserType[];
}

const MaintenanceRepairList: React.FC<MaintenanceRepairListProps> = ({
  itemsPerPage = 10,
  isAdmin = false,
  pilots
}) => {
  const [logs, setLogs] = useState<MaintenanceLogType[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [logCount, setLogCount] = useState<number>(0);
  const [selectedLogNum, setSelectedLogNum] = useState<number>(0);

  const [sortColumn, setSortColumn] = useState<string>('flightNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const [selectedLog, setSelectedLog] = useState<MaintenanceLogType | null>(
    null
  );

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!logs || logs.length === 0) {
      setCurrentPage(1);
      return;
    }
    if (currentPage > Math.ceil(logs.length / itemsPerPage)) {
      setCurrentPage(1);
    }
  }, [logs, itemsPerPage, currentPage]);

  useEffect(() => {
    loadData();
  }, [itemsPerPage]);

  async function loadData() {
    console.log('load');
    const tempCount = await getMaintenanceLogCount([]);
    setTotalPages(Math.ceil(tempCount / itemsPerPage));
    setLogCount(tempCount);
    console.log(tempCount);
    await loadInitPage();
    console.log('load finished');
  }
  async function loadInitPage() {
    setIsLoading(true);
    const tempLogs = await loadMaintenanceLogs(itemsPerPage, undefined, []);
    if (tempLogs === undefined) {
      setIsLoading(false);
      return;
    }
    setLogs(tempLogs);
    setIsLoading(false);
  }
  async function loadNextPage() {
    setIsLoading(true);
    const tempLogs = await loadMaintenanceLogs(
      itemsPerPage,
      logs[logs.length - 1],
      []
    );
    if (tempLogs === undefined) {
      setIsLoading(false);
      return;
    }
    setLogs((currentLogs) => [...currentLogs, ...tempLogs]);
    setIsLoading(false);
  }

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  const sortedLogs = useMemo(() => {
    const newLogs = [...logs];
    newLogs.sort((a, b) => {
      let compareResult = 0;

      switch (sortColumn) {
        case 'flightNumber':
          const aNum = parseInt(a.logId, 10);
          const bNum = parseInt(b.logId, 10);
          compareResult = (aNum || 0) - (bNum || 0);
          break;

        case 'date':
          if (a.date && b.date) {
            compareResult =
              new Date(a.date).getTime() - new Date(b.date).getTime();
          } else if (a.date && !b.date) {
            compareResult = -1;
          } else if (!a.date && b.date) {
            compareResult = 1;
          }
          break;

        case 'flightDuration':
          if (a.droneType && b.droneType) {
            compareResult = a.droneType.localeCompare(b.droneType);
          } else if (a.droneType && !b.droneType) {
            compareResult = -1;
          } else if (!a.droneType && b.droneType) {
            compareResult = 1;
          }
          break;

        default:
          break;
      }
      return sortDirection === 'asc' ? compareResult : -compareResult;
    });

    return newLogs;
  }, [logs, sortColumn, sortDirection]);

  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedLogs.slice(startIndex, endIndex);
  }, [sortedLogs, currentPage, itemsPerPage]);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const goToNextPage = async () => {
    if (currentPage < totalPages) {
      if (logs.length < itemsPerPage * currentPage + 1) {
        await loadNextPage();
      }
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleRowClick = (log: MaintenanceLogType) => {
    setSelectedLog(log);
  };

  const onCloseModalRepair = async (log: MaintenanceLogType | undefined) => {
      if (log != undefined) {
        await saveMaintenanceLogs([log]);
        const tempLogs = [...logs];
        const index = tempLogs.findIndex(
          (element) => element.logId === log.logId
        );
        if(index == -1){
          tempLogs.push(log)
        }else{
          tempLogs[index] = log;
        }
        setLogs(tempLogs);
      } else if (selectedLog != undefined) {
        await deleteMaintenanceLog(selectedLog.logId);
        const tempLogs = logs.filter(
          (element) => element.logId !== selectedLog.logId
        );
        setLogs(tempLogs);
      }
      setSelectedLog(null);
      setSelectedLogNum(0);
    };
  

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
    useEffect(() => {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

  let computedColSpan = 6; 
  if (windowWidth <= 480) {
    computedColSpan = 3; 
  } else if (windowWidth <= 600) {
    computedColSpan = 4; 
  } else if (windowWidth <= 900) {
    computedColSpan = 4; 
  } else if (windowWidth <= 1200) {
    computedColSpan = 5; 
  } else {
    computedColSpan = 5;
  }

  return (
    <>
      <div className="maintenance-log-table-container-repair">
        <table className="maintenance-log-table-repair">
          <thead>
            <tr>
              <th
                className="maintenance-clickable-header"
                onClick={() => handleSort('date')}
              >
                <div>Datum</div>
              </th>
              <th>
                <div>Autor</div>
              </th>
              <th
                className="maintenance-clickable-header"
                onClick={() => handleSort('flightDuration')}
              >
                <div>Drohne</div>
              </th>
              <th>
                <div>Typ</div>
              </th>
              <th>
                <div>kritische Fehler</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedLogs.length === 0 && (
              <tr>
                <td
                  colSpan={computedColSpan}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  <img
                    src="/img/no-logs.svg"
                    alt="No logs available"
                    style={{ width: '150px', marginBottom: '20px' }}
                  />
                  <p>Keine Wartungs-Logs vorhanden</p>
                </td>
              </tr>
            )}

            {paginatedLogs.map((log) => {
              return (
                <tr
                  key={log.logId}
                  onClick={() => handleRowClick(log)}
                  className="clickable-row"
                >
                  <td>
                    <div className="cell-content">
                      {log.date ? log.date.toLocaleDateString() : 'Unbekannt'}
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">
                      {pilots.some(
                                (pilot) => pilot.email === log.createdBy
                              )
                            ? pilots
                                .filter((pilot) => pilot.email === log.createdBy)
                                .map(
                                  (pilot) =>
                                    `${pilot.prename[0]}.${pilot.lastname}`
                                )
                            : 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">
                      {log.droneType ? log.droneType : 'Unbekannt'}
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">
                      {log.maintenanceType ? log.maintenanceType : 'Unbekannt'}
                    </div>
                  </td>
                  <td>
                    <div className="cell-content">
                      {log.errors ? log.errors : 'Unbekannt'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          className="pagination-button"
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          <span className="material-icons-pagination">arrow_back</span>
        </button>
        <span className="pagination-info">
          Seite {currentPage} von {totalPages}
        </span>
        <button
          className="pagination-button"
          onClick={goToNextPage}
          disabled={currentPage === totalPages || isLoading}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <span className="material-icons-pagination">arrow_forward</span>
            </>
          )}
        </button>
      </div>

      {selectedLog && (
        <MaintenanceRepairDetailModal
          log={selectedLog}
          onClose={onCloseModalRepair}
          isAdmin={isAdmin}
          pilots={[{ email: 'N/A', prename: 'N/A', lastname: '' }, ...pilots]}
        />
      )}
    </>
  );
};

export default MaintenanceRepairList;
